import { clone, omit } from "lodash";

import {
  object, number, string, array
} from "../../../../../../util/yup";
import { WEEK_DAYS } from "../../../../../../util/workWeek";

function getFilenameFromUrl(url) {
  if (!url) return null;
  try {
    const { pathname } = new URL(url);
    const filename = pathname.split("/").slice(-1)[0];
    return decodeURIComponent(filename);
  } catch (e) {
    return url.split("/").slice(-1)[0];
  }
}

export const defaultRows = [
  {
    id: "Standard", total: 0, monday: "", tuesday: "", wednesday: "", thursday: "", friday: "", saturday: "0", sunday: "0"
  },
];

export const otRoles = [
  {
    id: "Overtime", total: 0, monday: "", tuesday: "", wednesday: "", thursday: "", friday: "", saturday: "", sunday: ""
  },
  {
    id: "Double Time", total: 0, monday: "", tuesday: "", wednesday: "", thursday: "", friday: "", saturday: "", sunday: ""
  },
];

export const excludeKeys = ([key]) => {
  return !["id", "total", "error"].includes(key);
};

const days = WEEK_DAYS;

function validateTimesheetRows(rows) {
  for (const row of Object.values(rows)) {
    if (row.error) {
      return row.error;
    }
  }
  const notFilled = Object.entries(rows[0])
    .filter(excludeKeys)
    .some(([, value]) => !Boolean(value));
  if (notFilled) {
    return "Standard Hours row must be filled";
  }
  return null;
}

export const validationSchema = object().shape({
  timesheetHours: array().of(object())
    .test({
      test(rows) {
        const error = validateTimesheetRows(rows);
        return error ? this.createError({ message: error }) : true;
      }
    }),
  additionalBilling: number()
    .nullable(true)
    .max(5000)
    .label("Additional Billing")
    .when("additionalBillingReason", {
      is: (val) => val,
      then: number().required()
    }),
  additionalBillingReason: string()
    .nullable(true)
    .max(1000)
    .label("Additional Billing Explanation")
    .when("additionalBilling", {
      is: (val) => val,
      then: string().required()
    }),
}, ['additionalBilling', 'additionalBillingReason']);

export const allocatableValidationSchema = object().shape({
  allocations: array()
    .test({
      test(allocations) {
        if (!allocations) return true;
        const codes = allocations.map((a) => a.code).filter(Boolean);
        const dupes = codes.filter((c, i) => codes.indexOf(c) !== i);
        if (dupes.length > 0) {
          return this.createError({ message: `Duplicate allocation code: ${dupes[0]}` });
        }
        for (const item of allocations) {
          const rows = item.timesheetHours;
          if (!rows || rows.length === 0) continue;
          // Only check for cell-level errors (e.g. daily > 24h), not "must be filled"
          for (const row of rows) {
            if (row.error) {
              return this.createError({ message: `${item.code}: ${row.error}` });
            }
          }
          if (item.additionalBilling && !item.additionalBillingReason) {
            return this.createError({ message: `${item.code}: Additional Billing Explanation is required` });
          }
          if (item.additionalBillingReason && !item.additionalBilling) {
            return this.createError({ message: `${item.code}: Additional Billing is required` });
          }
        }
        return true;
      }
    }),
});

function buildRowsFromProjectData(projectData) {
  const rows = [];

  const stdDetails = projectData.standardHoursDetails;
  if (stdDetails && stdDetails.length === 7) {
    const stdRow = { id: "Standard" };
    days.forEach((day, i) => { stdRow[day] = stdDetails[i] ?? "0"; });
    stdRow.total = days.reduce((sum, day) => sum + (parseFloat(stdRow[day]) || 0), 0);
    rows.push(stdRow);
  } else {
    rows.push({ ...defaultRows[0] });
  }

  const otDetails = projectData.nonStandardHoursDetails;
  if (otDetails && otDetails.length === 7) {
    const otRow = { id: "Overtime" };
    days.forEach((day, i) => { otRow[day] = otDetails[i] ?? "0"; });
    otRow.total = days.reduce((sum, day) => sum + (parseFloat(otRow[day]) || 0), 0);
    if (otRow.total > 0) rows.push(otRow);
  }

  const dtDetails = projectData.doubleTimeHoursDetails;
  if (dtDetails && dtDetails.length === 7) {
    const dtRow = { id: "Double Time" };
    days.forEach((day, i) => { dtRow[day] = dtDetails[i] ?? "0"; });
    dtRow.total = days.reduce((sum, day) => sum + (parseFloat(dtRow[day]) || 0), 0);
    if (dtRow.total > 0) rows.push(dtRow);
  }

  return rows;
}

export function buildInitialValues(payPeriod, showAllocationForm, contractAllocations = []) {
  const { attributes } = payPeriod;

  const timesheetHours = [];

  // For zero hour weeks, we still need to show the row
  if (attributes?.submittedAt !== null || attributes?.disputedAt !== null) {
    timesheetHours.push({
      id: "Standard",
      total: parseFloat(attributes.standardHours ?? "0"),
      monday: attributes.standardHoursDetails?.[0] ?? "N/A",
      tuesday: attributes.standardHoursDetails?.[1] ?? "N/A",
      wednesday: attributes.standardHoursDetails?.[2] ?? "N/A",
      thursday: attributes.standardHoursDetails?.[3] ?? "N/A",
      friday: attributes.standardHoursDetails?.[4] ?? "N/A",
      saturday: attributes.standardHoursDetails?.[5] ?? "N/A",
      sunday: attributes.standardHoursDetails?.[6] ?? "N/A",
    });
  } else {
    // If hours are not submitted, use default row
    timesheetHours.push(defaultRows[0]);
  }

  if (attributes?.nonStandardHours !== null && parseFloat(attributes.nonStandardHours) > 0) {
    timesheetHours.push({
      id: "Overtime",
      total: parseFloat(attributes.nonStandardHours),
      monday: attributes.nonStandardHoursDetails?.[0] ?? "N/A",
      tuesday: attributes.nonStandardHoursDetails?.[1] ?? "N/A",
      wednesday: attributes.nonStandardHoursDetails?.[2] ?? "N/A",
      thursday: attributes.nonStandardHoursDetails?.[3] ?? "N/A",
      friday: attributes.nonStandardHoursDetails?.[4] ?? "N/A",
      saturday: attributes.nonStandardHoursDetails?.[5] ?? "N/A",
      sunday: attributes.nonStandardHoursDetails?.[6] ?? "N/A",
    });
  }

  if (attributes?.doubleTimeHours !== null && parseFloat(attributes.doubleTimeHours) > 0) {
    timesheetHours.push({
      id: "Double Time",
      total: parseFloat(attributes.doubleTimeHours),
      monday: attributes.doubleTimeHoursDetails?.[0] ?? "N/A",
      tuesday: attributes.doubleTimeHoursDetails?.[1] ?? "N/A",
      wednesday: attributes.doubleTimeHoursDetails?.[2] ?? "N/A",
      thursday: attributes.doubleTimeHoursDetails?.[3] ?? "N/A",
      friday: attributes.doubleTimeHoursDetails?.[4] ?? "N/A",
      saturday: attributes.doubleTimeHoursDetails?.[5] ?? "N/A",
      sunday: attributes.doubleTimeHoursDetails?.[6] ?? "N/A",
    });
  }

  const documentValue = attributes?.documentUrl
    ? { name: getFilenameFromUrl(attributes.documentUrl) }
    : null;

  const result = {
    timesheetHours,
    additionalBilling: attributes?.additionalBilling && parseFloat(attributes.additionalBilling),
    additionalBillingReason: attributes?.additionalBillingReason,
    document: documentValue,
  };

  if (showAllocationForm) {
    const allocationCodes = attributes.allocationCodes || [];
    const existingAllocations = attributes.allocations || {};
    const isSubmitted = attributes.submittedAt !== null || attributes.disputedAt !== null;

    const getType = (code) => {
      const alloc = (contractAllocations || []).find(a => a.code === code);
      return alloc?.type || "project";
    };

    result.allocations = allocationCodes.map((code) => {
      const existing = existingAllocations[code];
      const type = getType(code);
      if (existing && isSubmitted) {
        return {
          code,
          type,
          timesheetHours: buildRowsFromProjectData(existing),
          additionalBilling: existing.additionalBilling != null ? parseFloat(existing.additionalBilling) : null,
          additionalBillingReason: existing.additionalBillingReason || "",
        };
      }
      return {
        code,
        type,
        timesheetHours: [{ ...defaultRows[0] }],
        additionalBilling: null,
        additionalBillingReason: "",
      };
    });
  }

  return result;
}

// Always emits Monday-first, whatever order the grid happens to render the days in.
function rowToHoursArray(row) {
  return days.map((day) => row[day] || "0");
}

function transformBaseHours(timesheetHours) {
  const result = {};

  const standardHours = timesheetHours.find(({ id }) => id === "Standard");
  if (standardHours.total === 0) {
    result.standardHours = null;
  } else {
    result.standardHours = standardHours.total;
  }
  result.standardHoursDetails = rowToHoursArray(timesheetHours[0]);

  const overtimeHours = timesheetHours.find(({ id }) => id === "Overtime");
  if (!overtimeHours || overtimeHours.total === 0) {
    result.nonStandardHours = null;
    result.nonStandardHoursDetails = null;
  } else {
    result.nonStandardHours = overtimeHours.total;
    result.nonStandardHoursDetails = rowToHoursArray(overtimeHours);
  }

  const doubleTimeHours = timesheetHours.find(({ id }) => id === "Double Time");
  if (!doubleTimeHours || doubleTimeHours.total === 0) {
    result.doubleTimeHours = null;
    result.doubleTimeHoursDetails = null;
  } else {
    result.doubleTimeHours = doubleTimeHours.total;
    result.doubleTimeHoursDetails = rowToHoursArray(doubleTimeHours);
  }

  return result;
}

export const transformValues = (values) => {
  const { allocations } = values;

  if (allocations && Array.isArray(allocations) && allocations.length > 0) {
    return transformAllocatableValues(values);
  }
  return transformNonAllocatableValues(values);
};

function transformNonAllocatableValues(values) {
  const newValues = clone(omit(values, ["timesheetHours"]));
  const { timesheetHours, additionalBilling } = values;

  Object.assign(newValues, transformBaseHours(timesheetHours));

  if (additionalBilling === 0) newValues.additionalBilling = null;

  return newValues;
}

function transformAllocatableValues(values) {
  const newValues = clone(omit(values, ["timesheetHours", "allocations"]));
  const { allocations } = values;

  // Build per-allocation output and compute base value sums
  const allocationsOutput = {};
  const summedStandard = new Array(7).fill(0);
  const summedOvertime = new Array(7).fill(0);
  const summedDoubleTime = new Array(7).fill(0);
  let totalAdditionalBilling = 0;

  for (const item of allocations) {
    const { code, type, timesheetHours: rows, additionalBilling: projBilling, additionalBillingReason: projReason } = item;

    const standardRow = rows.find(r => r.id === "Standard");
    const overtimeRow = rows.find(r => r.id === "Overtime");
    const doubleTimeRow = rows.find(r => r.id === "Double Time");

    const stdDetails = rowToHoursArray(standardRow || rows[0]);
    const otDetails = overtimeRow && overtimeRow.total > 0 ? rowToHoursArray(overtimeRow) : null;
    const dtDetails = doubleTimeRow && doubleTimeRow.total > 0 ? rowToHoursArray(doubleTimeRow) : null;

    stdDetails.forEach((v, i) => { summedStandard[i] += parseFloat(v) || 0; });
    if (otDetails) otDetails.forEach((v, i) => { summedOvertime[i] += parseFloat(v) || 0; });
    if (dtDetails) dtDetails.forEach((v, i) => { summedDoubleTime[i] += parseFloat(v) || 0; });

    totalAdditionalBilling += (projBilling || 0);

    allocationsOutput[code] = {
      code,
      type: type || "project",
      standard_hours_details: stdDetails,
      non_standard_hours_details: otDetails,
      double_time_hours_details: dtDetails,
      additional_billing: projBilling || null,
      additional_billing_reason: projReason || null,
    };
  }

  // Set base values from sums
  const totalStd = summedStandard.reduce((a, b) => a + b, 0);
  newValues.standardHours = totalStd > 0 ? totalStd : null;
  newValues.standardHoursDetails = summedStandard.map(v => v.toString());

  const totalOt = summedOvertime.reduce((a, b) => a + b, 0);
  newValues.nonStandardHours = totalOt > 0 ? totalOt : null;
  newValues.nonStandardHoursDetails = totalOt > 0 ? summedOvertime.map(v => v.toString()) : null;

  const totalDt = summedDoubleTime.reduce((a, b) => a + b, 0);
  newValues.doubleTimeHours = totalDt > 0 ? totalDt : null;
  newValues.doubleTimeHoursDetails = totalDt > 0 ? summedDoubleTime.map(v => v.toString()) : null;

  newValues.additionalBilling = totalAdditionalBilling > 0 ? totalAdditionalBilling : null;
  // allocations is handled separately in submit to preserve allocation code keys
  newValues.allocations = allocationsOutput;

  return newValues;
}

export function computeTotalsFromAllocations(allocations) {
  const summedRows = {
    Standard: { id: "Standard", total: 0 },
    Overtime: { id: "Overtime", total: 0 },
    "Double Time": { id: "Double Time", total: 0 },
  };

  days.forEach((day) => {
    summedRows.Standard[day] = "0";
    summedRows.Overtime[day] = "0";
    summedRows["Double Time"][day] = "0";
  });

  let totalAdditionalBilling = 0;
  let hasOvertime = false;
  let hasDoubleTime = false;

  for (const item of (allocations || [])) {
    const rows = item.timesheetHours || [];

    for (const row of rows) {
      const target = summedRows[row.id];
      if (!target) continue;

      if (row.id === "Overtime") hasOvertime = true;
      if (row.id === "Double Time") hasDoubleTime = true;

      days.forEach((day) => {
        const val = parseFloat(row[day]) || 0;
        target[day] = (parseFloat(target[day]) + val).toString();
      });
      target.total += (row.total || 0);
    }

    totalAdditionalBilling += (item.additionalBilling || 0);
  }

  const resultRows = [summedRows.Standard];
  if (hasOvertime) resultRows.push(summedRows.Overtime);
  if (hasDoubleTime) resultRows.push(summedRows["Double Time"]);

  return { totalRows: resultRows, totalAdditionalBilling };
}
